import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import {
  hasActiveTabSession,
  establishTabSession,
  clearTabSession,
} from '../../utils/tabSession';
import { clearAllAiAssistantChats } from '../../utils/aiAssistantChatStorage';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password, remember }, { rejectWithValue }) => {
    try {
      const response = await authService.login(username, password, remember);
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const retryAfterSeconds = Number(error.response?.data?.retryAfterSeconds) || 0;

      let errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Invalid username or password. Please try again.';

      // Axios "Network Error" (often Chrome status: blocked) — no HTTP response.
      if (!error.response && (error.message === 'Network Error' || error.code === 'ERR_NETWORK')) {
        errorMessage =
          'Network error. Please check your internet connection and try again.';
      } else if (status === 423) {
        errorMessage =
          error.response?.data?.message ||
          'Your practice has been locked. Please contact administrator.';
      }

      if (retryAfterSeconds > 0) {
        return rejectWithValue({ message: errorMessage, retryAfterSeconds });
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ otp }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOTP(otp);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'OTP verification failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get user');
    }
  }
);

export const checkSession = createAsyncThunk(
  'auth/checkSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.checkSession();
      // Expecting { authenticated: true, user: {...} } or { authenticated: false }
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      // If unauthorized, return explicit expired state
      if (status === 401) {
        return { authenticated: false, expired: true };
      }
      // For other network/server errors, reject so we can handle it
      return rejectWithValue(error.response?.data?.message || 'Session verification failed');
    }
  }
);

/** App startup: require a tab-scoped session or force login after tab/browser close. */
export const bootstrapAuth = createAsyncThunk(
  'auth/bootstrapAuth',
  async (_, { rejectWithValue }) => {
    const path = typeof window !== 'undefined' ? String(window.location.pathname || '') : '';
    const isStaticUploadView =
      path.startsWith('/uploads/') || path.startsWith('/api/uploads/');

    if (!hasActiveTabSession()) {
      // Opening an upload URL in a new tab has no tab session. Do not logout()
      // the shared cookie or the original tab is signed out.
      if (!isStaticUploadView) {
        try {
          await authService.logout();
        } catch {
          // No server session or network error — still show login.
        }
      }
      return { authenticated: false, tabSessionMissing: true };
    }

    try {
      const response = await authService.checkSession();
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        clearTabSession();
        return { authenticated: false, expired: true };
      }
      return rejectWithValue(error.response?.data?.message || 'Session verification failed');
    }
  }
);

// Check localStorage for basic preferences only, not authentication truth
const getInitialAuthState = () => {
  return {
    user: null,
    isAuthenticated: false,
    isLoading: true, // Start in loading state for mandatory silent bootstrap
    isBootstrapped: false, // Track initial check status
    error: null,
    requiresOTP: false,
    sessionExpired: false,
    showWelcomeMessage: false,
  };
};

const initialState = getInitialAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setRequiresOTP: (state, action) => {
      state.requiresOTP = action.payload;
    },
    clearWelcomeMessage: (state) => {
      state.showWelcomeMessage = false;
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.requiresOTP = false;
      state.sessionExpired = false;
      state.showWelcomeMessage = false;
      state.error = null;

      clearTabSession();

      // Clear localStorage
      try {
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
        clearAllAiAssistantChats();
      } catch (error) {
        // Error clearing auth state
      }
    },
    updateUserSessionTime: (state, action) => {
      if (state.user) {
        state.user.session_time = action.payload;
        
        try {
          localStorage.setItem('user', JSON.stringify(state.user));
        } catch (error) {}
      }
    },
    updateUserAiBasedAnalysis: (state, action) => {
      if (state.user) {
        state.user.ai_based_analysis_enabled = Number(action.payload) === 0 ? 0 : 1;

        try {
          localStorage.setItem('user', JSON.stringify(state.user));
        } catch (error) {}
      }
    },
    resetAuthUiState: (state) => {
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        establishTabSession();
        if (action.payload.requires_otp) {
          state.requiresOTP = true;
        } else {
          state.user = action.payload.user;
          state.isAuthenticated = true;
          state.requiresOTP = false;
          state.showWelcomeMessage = true;

          // Store authentication state in localStorage
          try {
            localStorage.setItem('user', JSON.stringify(action.payload.user));
            localStorage.setItem('isAuthenticated', 'true');
          } catch (error) {
            // Error storing auth state
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.requiresOTP = false;
      })

      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        establishTabSession();
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.requiresOTP = false;
        state.showWelcomeMessage = true;
        try {
          localStorage.setItem('user', JSON.stringify(action.payload.user));
          localStorage.setItem('isAuthenticated', 'true');
        } catch (error) {}
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.requiresOTP = false;
        state.sessionExpired = false;
        state.showWelcomeMessage = false;
        state.error = null;

        clearTabSession();

        // Clear localStorage
        try {
          localStorage.removeItem('user');
          localStorage.removeItem('isAuthenticated');
          clearAllAiAssistantChats();
        } catch (error) {
          // Error clearing auth state
        }
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      })

      // Bootstrap (tab-scoped session on app load)
      .addCase(bootstrapAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(bootstrapAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isBootstrapped = true;

        if (action.payload && (action.payload.authenticated || action.payload.valid)) {
          state.isAuthenticated = true;
          state.sessionExpired = false;
          state.user = action.payload.user || state.user;

          try {
            localStorage.setItem('user', JSON.stringify(state.user));
            localStorage.setItem('isAuthenticated', 'true');
          } catch (error) {}
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.requiresOTP = false;
          state.sessionExpired = action.payload?.expired || false;

          try {
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            clearAllAiAssistantChats();
          } catch (error) {}
        }
      })
      .addCase(bootstrapAuth.rejected, (state) => {
        state.isLoading = false;
        state.isBootstrapped = true;
        state.isAuthenticated = false;
        state.user = null;

        try {
          localStorage.removeItem('user');
          localStorage.removeItem('isAuthenticated');
          clearAllAiAssistantChats();
        } catch (error) {}
      })

      // Check Session
      .addCase(checkSession.pending, (state, action) => {
        // Only set isLoading=true if this is NOT a background ping
        if (!action.meta.arg?.isBackground) {
          state.isLoading = true;
        }
      })
      .addCase(checkSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isBootstrapped = true;

        if (action.payload && (action.payload.authenticated || action.payload.valid)) {
          state.isAuthenticated = true;
          state.sessionExpired = false;
          state.user = action.payload.user || state.user;

          // Sync localStorage
          try {
            localStorage.setItem('user', JSON.stringify(state.user));
            localStorage.setItem('isAuthenticated', 'true');
          } catch (error) {}
        } else {
          // Explicitly clear state if session is invalid
          state.isAuthenticated = false;
          state.user = null;
          state.sessionExpired = action.payload?.expired || false;

          if (action.payload?.expired) {
            clearTabSession();
          }
          
          try {
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            clearAllAiAssistantChats();
          } catch (error) {}
        }
      })
      .addCase(checkSession.rejected, (state, action) => {
        state.isLoading = false;
        state.isBootstrapped = true;
        
        // On rejection (network failure, etc.), we should default to logged out for safety
        // Unless it's a transient background error
        if (!action.meta.arg?.isBackground) {
          state.isAuthenticated = false;
          state.user = null;
          
          try {
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            clearAllAiAssistantChats();
          } catch (error) {}
        }
      });
  },
});

export const {
  clearError,
  setRequiresOTP,
  clearWelcomeMessage,
  clearAuth,
  updateUserSessionTime,
  updateUserAiBasedAnalysis,
  resetAuthUiState,
} = authSlice.actions;

export default authSlice.reducer;
